/** Visitors Desk API client. Types mirror the `/api/visitors/desk/*` responses
 *  (aamcirc/admin/routers/visitors_desk.py and aamcirc/visitors/desk/*.py).
 *  Dates are ISO strings. A rate is a fraction (0.41), or null when its base
 *  is under 30; counts are always kept. */
import { api } from './client'
import { deskQuery, type DeskGroup, type DeskQuery } from '../lib/deskPeriod'

export type DeskPub = 'all' | 'flight_journal' | 'model_airplane_news' | 'rc_car_action' | 'diecast'
export type Verdict = 'normal' | 'above' | 'below' | 'collecting'
export type DeskSource = 'first_party' | 'gsc' | 'ga4'

export interface PeriodMeta {
  kind: string; start: string; end: string; days: number; incomplete: boolean; live: boolean
  clamped: boolean; clipped: boolean
  previous: [string, string] | null; last_year: [string, string] | null
}
export interface Meta {
  publication: string
  titles_included: string[]
  titles_excluded: { publication: string; reason: string }[]
  period: PeriodMeta | null
  compare: string
  source: DeskSource
  through: string | null
  notes: string[]
  status_approx?: boolean
  status_first_live?: string | null
}
export interface NormalRange { low: number; high: number; mean: number; periods: number }
/** One measure against the previous window, last year and its normal range. */
export interface Compared {
  value: number | null
  previous: number | null
  change: number | null
  change_pct: number | null
  last_year: number | null
  last_year_change_pct: number | null
  last_year_change: number | null
  last_year_source: DeskSource | null
  last_year_label: string | null
  verdict: Verdict | null
  normal: NormalRange | null
  /** A rate: `change` is in the rate's own unit (a fraction), shown as points. */
  points: boolean
  incomplete: boolean
}

/** Every screen route answers 200 with `unavailable` text (and no data) when the title lacks the source. */
export type Desk<T> = { meta: Meta; group_applied?: boolean } & ({ unavailable: string } | (T & { unavailable?: undefined }))

// ---- Today ----
export interface Tile { key: string; label: string; compared: Compared; sub: string | null; source: DeskSource; context: string | null }
export interface LadderRung {
  rung: 'flyby' | 'occasional' | 'regular' | 'known' | 'member'; label: string; definition: string
  people: number | null; share: number | null; moved_up: number | null; moved_down: number | null; new_people: number | null
}
export interface Rate { rate: number | null; numerator: number | null; denominator: number | null }
export interface LadderView {
  day: string; rolled_up: boolean; rungs: LadderRung[]
  engaged: { people: number | null; share_of_members: number | null }
  not_seen: { members: number | null; known: number | null }
  step_up: { flyby_occasional: Rate; occasional_regular: Rate; regular_member: Rate; member_engaged: Rate }
}
export interface TitleRow {
  publication: string; people: number; regular: number; known: number; members: number; members_active: number
  regular_to_member: Rate; shape: number[]
}
export interface ReconRow { day: string; first_party_people: number | null; ga4_people: number | null; ga4_engaged_sessions: number | null; drift: number | null; comparable: boolean; flagged: boolean }
export interface BotPanel {
  raw_visits: number; human_visits: number; hidden: number; bot_share: number | null
  by_verdict: { unconfirmed: number; automated: number }
  previous_share: number | null
  reasons: { reason: string; visits: number }[]
  reconciliation: { publication: string; rows: ReconRow[]; flagged_days: number }[]
  reconciliation_note?: string
  note: string
}
export interface Mover { key: string; label: string; current: number; previous: number; change: number; href: string; publication?: string }
export interface MoverList { up: Mover[]; down: Mover[] }
export interface Movers { sources: MoverList; articles: MoverList; search_terms: MoverList; note: string }
export type ChangeKind = 'outside_normal' | 'new_source' | 'spike' | 'issue' | 'search' | 'mover' | 'alert'
export interface ChangeItem { kind: ChangeKind; tag: string; text: string; link_text: string; href: string; publication: string; subject?: string; direction?: string | null; rule?: string }

// ---- Sources ----
export interface SourceRow {
  key: string; label: string; level: 'channel' | 'detail' | 'host'; parent: string | null; people: number; share: number | null
  change_pct: number | null; visits: number; engaged_rate: number | null; pages_per_visit: number | null
  became_regular: number; signed_up: number; members_gained: number
}
export interface ReferrerRow { referrer_url: string; label: string; landing: string; people: number; became_regular: number; joined: number }
export interface NewSource { host: string; first_seen: string; people: number; top_landing: string | null }
export interface EmailSend {
  publication: string; label: string; kind: string; sent_at: string; sent: number; opened_rate: number | null
  visits: number; usual_visits: number | null; vs_usual_pct: number | null; paid: number
}
export interface SocialRow { network: string; channel: string; people: number; change_pct: number | null; visits: number; engaged_rate: number | null; top_landing: string | null }
export interface SourcesResp { rows: SourceRow[]; direct_note: string }

// ---- Search ----
export interface SearchTile { key: 'clicks' | 'impressions' | 'position' | 'discover_clicks'; label: string; compared: Compared }
export interface SearchRow {
  query: string | null; page_url: string | null; page_key: string | null; impressions: number; clicks: number
  ctr: number | null; expected_ctr: number | null; position: number | null; prev_clicks: number | null
  prev_position: number | null; change: number | null; change_pct: number | null; brand: boolean; move: string | null
  publication: string
}
export type SearchView = 'close' | 'ctr_gap' | 'rising' | 'falling' | 'brand' | 'topic' | 'discover' | 'news'
export interface BrandSide { clicks: number; impressions: number; queries: number }
export interface CompetingRow { query: string; impressions: number; clicks: number; pages: SearchRow[]; move: string; publication: string }
export interface SiteSearchRow {
  publication: string; surface: 'topic' | 'archive' | 'editions' | 'cars'; q: string; label: string
  searches: number; zero_results: number; results: number | null; results_display: string; next_path: string | null
}

// ---- Flows ----
export interface FlowNode { key: string; label: string; visits: number }
export interface FlowColumn { name: string; column: 'channel' | 'landing' | 'next_path' | 'outcome'; nodes: FlowNode[] }
export interface FlowPath { steps: string[]; outcome: string; outcome_label: string; visits: number }
export interface LandingRow {
  landing: string; title: string | null; mostly_from: string; arrivals: number; second_page_rate: number | null
  second_page: number; low_recirculation: boolean; top_next: string | null; top_next_title: string | null
  paywall_rate: number | null; paywall_hits: number | null; views: number | null; signed_up: number; joined: number
}
export interface ExitRow { path: string; title: string | null; exits: number; share_of_views: number | null; views: number | null; reason: string | null }
export interface OutboundRow { url: string; from_path: string; from_title: string | null; clicks: number; kind: string }

// ---- Articles ----
export type Ranking = 'most_read' | 'brings_in' | 'keeps_members' | 'converts'
export interface ArticleRow {
  article_id: number; publication: string; title: string | null; section: string; published: string | null
  access: 'free' | 'members'; people: number; real_reads: number; views: number
  read_end_rate: number | null; read_end: number; recirculation_rate: number | null; entries: number
  paywall_rate: number | null; paywall_hits: number; members_gained: number
  who: { anonymous: number; known: number; member: number }; score: number
}
export interface SectionRow { name: string; articles: number; people_per_article: number; read_end_rate: number | null; members_gained: number; people: number }
export interface OldArticleRow { article_id: number; publication: string; title: string | null; published: string | null; people: number; mostly_from: string | null }
export interface ArticleDetail {
  article: { article_id: number; publication: string; title: string | null; slug: string; path: string; section: string; tags: string[]; published: string | null; access: 'free' | 'members' }
  totals: Omit<ArticleRow, 'score'> | null
  daily: { day: string; people: number; real_reads: number; read_end: number; entries: number }[]
  sources: { channel: string; label: string; visits: number }[]
  search_terms: { query: string; clicks: number; impressions: number; position: number | null }[]
  next_pages: { path: string; visits: number }[]
  last_read_before_joining: number
  named_readers?: { subscriber_id: number; name: string | null; last_read_at: string }[]
}

// ---- Issues ----
export interface IssueListItem { issue_id: number; title: string; released: string; publication: string; publication_name: string }
export interface IssueRow {
  issue_id: number; title: string; released: string; members_reached_share: number | null; opened: number
  downloads: number; median_pages: number | null; days_to_15pct: string | null; opens_in_period: number
  publication: string; publication_name: string
}
export interface IssuesResp {
  issues: IssueListItem[]
  all: { rows: IssueRow[]; period_opens: number; back_issue_opens: number; back_issue_share: number | null }
  heading: string; back_note: string
}
export interface IssueDetail {
  tiles: {
    issue_id: number; title: string; released: string; day: number; as_of: string; members: number
    previous_issue: { issue_id: number; title: string; released: string } | null
    pages_total: number | null
    opened_members: { value: Compared; share: Compared }
    downloads: Compared; median_pages: Compared
    device: { base: number; phone: Compared; tablet: Compared; desktop: Compared; counts: { phone: number; tablet: number; desktop: number } }
  }
  pickup: {
    series: { issue_id: number; title: string; released: string; points: (number | null)[] }[]
    email_sent_day: number | null; email_before_release: boolean; members: number
  } | null
  pickup_note: string
  depth: { tenths: (number | null)[]; openers: number }
  spreads: { pages: string; what: string; readers: number; median_time_ms: number | null }[]
  spreads_note: string
}

// ---- Audience ----
export interface AudienceResp {
  new_vs_returning: {
    weeks: { week_start: string; new: number; returning: number; incomplete: boolean }[]
    back_within_7d: Rate & { weeks_covered: string[] }
    back_within_28d: Rate & { weeks_covered: string[] }
  }
  frequency: { label: string; people: number; share: number | null }[]
  locations: {
    region_scope: string
    countries: { code: string; label: string; people: number; share: number | null }[]
    regions: { region: string; code: string | null; country: string | null; people: number; visits: number; share: number | null }[]
  }
  devices: { device: string; people: number; share: number | null; visits: number; real_read_rate: number | null }[]
  who: { access: string; label: string; people: number; pages: number; pages_per_person: number | null; recent?: number; older?: number }[]
  heat: { days: string[]; blocks: string[]; cells: number[][] }
}

// ---- Trends ----
export type TrendMetric = 'people' | 'regular' | 'known_new' | 'members_gained' | 'members_active' | 'google_clicks' | 'social_visits' | 'email_visits' | 'issue_opens' | 'bot_share'
export interface TrendPoint {
  day: string; value: number | null; titles_counted: number; rolling28: number | null
  low: number | null; high: number | null; incomplete: boolean
}
export interface Annotation { day: string; rule: string; label: string; text: string; href: string; publication: string; direction: string | null }
export interface AlertRuleCard { rule: string; label: string; text: string; enabled: boolean; last_fired: string | null }
export interface FiredAlert {
  id: number; rule: string; label: string; publication: string; day: string; subject: string; direction: string | null
  text: string; href: string; link_text: string; fired_at: string; emailed: boolean; payload: Record<string, unknown>
}
export interface AlertsResp { rules: AlertRuleCard[]; fired: FiredAlert[] }
export interface TrendSeries {
  metric: TrendMetric; label: string; kind: 'count' | 'rate'; start: string; end: string
  points: TrendPoint[]
  last_year: { day: string; value: number | null }[]
  ga4_history: { label: string; points: { day: string; value: number | null }[] } | null
  ga4_last_year: { label: string; points: { day: string; value: number | null }[] } | null
  annotations: Annotation[]
}
export interface ScoreRow {
  metric: TrendMetric; label: string; kind: 'count' | 'rate'; source: DeskSource
  now: number | null; previous: number | null; change: number | null; change_pct: number | null
  last_year: number | null; last_year_label: string | null; last_year_change: number | null; last_year_change_pct: number | null
  verdict: Verdict | null; incomplete: boolean; unavailable: string | null
}
export interface LadderMonth { month: string; day: string; to_date: boolean; people: Record<string, number> }

// ---- Settings ----
export interface SourceDomain { domain: string; grp: string; label?: string | null; [k: string]: unknown }

type Extra = Record<string, string | number | boolean | null | undefined>

function qs(extra?: Extra): string {
  if (!extra) return ''
  const parts = Object.entries(extra)
    .filter(([, v]) => v !== undefined && v !== null && v !== '' && v !== false)
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
  return parts.length ? `&${parts.join('&')}` : ''
}

const base = '/api/visitors/desk'
const groupQs = (g: DeskGroup) => (g === 'all' ? '' : `&group=${g}`)

function get<T>(path: string, pub: DeskPub | string, q: DeskQuery, extra?: Extra) {
  return api.get<Desk<T>>(`${base}/${path}?${deskQuery(pub, q)}${qs(extra)}`)
}

/** How fresh the data is: last rollup day, last event, events not yet grouped into visits. */
export interface Freshness { rollups_through: string | null; last_event_at: string | null; unsessioned: number }
/** One title's row from `/desk/availability`. */
export interface TitleAvail {
  publication: string
  first_party: boolean
  first_party_text: string | null
  gsc: boolean
  ga4: boolean
  freshness: Freshness
}
export interface Availability { titles: TitleAvail[]; all: Freshness }

// ---- Alert, recipient and digest settings (plan 4, admin only) ----
export interface ParamSpec { key: string; kind: 'int' | 'float' | 'bool'; default: number | boolean; lo: number | null; hi: number | null; label: string }
export interface AlertRuleView {
  rule: string; label: string; text: string; template: string; enabled: boolean
  params: Record<string, number | boolean>; param_specs: ParamSpec[]; hourly: boolean; help: string
}
export interface AlertSettings { rules: AlertRuleView[]; mail_enabled: boolean; alerts_email_on: boolean }
export type RecipientKind = 'alerts' | 'digest'
export interface Recipient { user_id: number; email: string; role: string }
export interface RecipientTitle {
  publication: string; title: string; default: boolean; user_ids: number[]
  effective: Recipient[]; candidates: Recipient[]
}
export interface DigestSettings {
  digest_on: boolean; alerts_email_on: boolean; mail_enabled: boolean; week_start: string; week_end: string
}
export type DigestPreview = { meta: Meta } & (
  | { unavailable: string }
  | { unavailable?: undefined; week_start: string; week_end: string; subject: string; html: string }
)

export const deskApi = {
  availability: () => api.get<Availability>(`${base}/availability`),
  // Today
  ladder: (pub: DeskPub, q: DeskQuery) => get<LadderView>('ladder', pub, q),
  tiles: (pub: DeskPub, q: DeskQuery) => get<{ tiles: Tile[] }>('tiles', pub, q),
  titles: (pub: DeskPub, q: DeskQuery) => get<{ rows: TitleRow[]; excluded: Meta['titles_excluded'] }>('titles', pub, q),
  bots: (pub: DeskPub, q: DeskQuery) => get<BotPanel>('bots', pub, q),
  todayMovers: (pub: DeskPub, q: DeskQuery) => get<Movers>('today/movers', pub, q),
  todayChanges: (pub: DeskPub, q: DeskQuery) =>
    get<{ items: ChangeItem[]; collecting: { kind: ChangeKind; note: string }[]; quiet: boolean }>('today/changes', pub, q),
  // Sources
  sources: (pub: DeskPub, q: DeskQuery) => get<SourcesResp>('sources', pub, q),
  sourceReferrers: (pub: DeskPub, q: DeskQuery, host: string) => get<{ host: string; rows: ReferrerRow[] }>('sources/referrers', pub, q, { host }),
  newSources: (pub: DeskPub, q: DeskQuery) => get<{ rows: NewSource[]; history_days: number; collecting: boolean }>('sources/new', pub, q),
  sourceEmails: (pub: DeskPub, q: DeskQuery) => get<{ rows: EmailSend[] }>('sources/email', pub, q),
  sourceSocial: (pub: DeskPub, q: DeskQuery) => get<{ rows: SocialRow[] }>('sources/social', pub, q),
  // Search
  search: (pub: DeskPub, q: DeskQuery) => get<{ tiles: SearchTile[]; data_through: string | null; behind_text: string }>('search', pub, q),
  searchTerms: (pub: DeskPub, q: DeskQuery, view: SearchView) =>
    get<{ view: SearchView; rows: SearchRow[]; note: string; coverage: { page_clicks: number | null; query_clicks: number | null; visible_share: number | null } }>('search/terms', pub, q, { view }),
  searchBrand: (pub: DeskPub, q: DeskQuery) =>
    get<{ brand: BrandSide | null; topic: BrandSide | null; anonymised: BrandSide | null; terms: Record<string, string[]> }>('search/brand', pub, q),
  searchCompeting: (pub: DeskPub, q: DeskQuery) => get<{ rows: CompetingRow[] }>('search/competing', pub, q),
  searchSite: (pub: DeskPub, q: DeskQuery, extra?: { zero_only?: boolean }) => get<{ rows: SiteSearchRow[] }>('search/site', pub, q, extra),
  // Flows (extra: channel, landing, next_path, outcome follow one stream)
  flows: (pub: DeskPub, q: DeskQuery, extra?: { channel?: string; landing?: string; next_path?: string; outcome?: string }) =>
    get<{ stream: Record<string, string>; heading: string; note: string; paths_heading: string; columns: FlowColumn[]; paths: FlowPath[] }>('flows', pub, q, extra),
  flowsLanding: (pub: DeskPub, q: DeskQuery) => get<{ heading: string; note: string; rows: LandingRow[] }>('flows/landing', pub, q),
  flowsExits: (pub: DeskPub, q: DeskQuery) => get<{ heading: string; rows: ExitRow[] }>('flows/exits', pub, q),
  flowsOutbound: (pub: DeskPub, q: DeskQuery) => get<{ heading: string; note: string; rows: OutboundRow[] }>('flows/outbound', pub, q),
  // Articles
  articles: (pub: DeskPub, q: DeskQuery, extra?: { ranking?: Ranking; section?: string; published_from?: string; published_to?: string; access?: 'free' | 'members' }) =>
    get<{ ranking: Ranking; ranking_note: string; rankings: { key: Ranking; label: string }[]; note: string; rows: ArticleRow[] }>('articles', pub, q, extra),
  articleSections: (pub: DeskPub, q: DeskQuery, by: 'section' | 'tag' = 'section') =>
    get<{ heading: string; by: string; rows: SectionRow[] }>('articles/sections', pub, q, { by }),
  articlesOld: (pub: DeskPub, q: DeskQuery) => get<{ heading: string; note: string; rows: OldArticleRow[] }>('articles/old', pub, q),
  article: (pub: DeskPub, q: DeskQuery, id: number) => get<ArticleDetail>(`articles/${id}`, pub, q),
  // Issues
  issues: (pub: DeskPub, q: DeskQuery) => get<IssuesResp>('issues', pub, q),
  issue: (pub: DeskPub, q: DeskQuery, id: number) => get<IssueDetail>(`issues/${id}`, pub, q),
  // Audience
  audience: (pub: DeskPub, q: DeskQuery) => get<AudienceResp>('audience', pub, q),
  // Trends
  trendsSeries: (pub: DeskPub, metric: TrendMetric, range?: { from?: string; to?: string }, group: DeskGroup = 'all') =>
    api.get<Desk<TrendSeries>>(`${base}/trends/series?publication=${encodeURIComponent(pub)}&metric=${metric}${qs(range)}${groupQs(group)}`),
  alerts: (pub: DeskPub, q: DeskQuery) => get<AlertsResp>('alerts', pub, q),
  trendsScorecard: (pub: DeskPub, q: DeskQuery) => get<{ rows: ScoreRow[] }>('trends/scorecard', pub, q),
  trendsLadder: (pub: DeskPub, months = 12, group: DeskGroup = 'all') =>
    api.get<Desk<{ rows: LadderMonth[] }>>(`${base}/trends/ladder?publication=${encodeURIComponent(pub)}&months=${months}${groupQs(group)}`),
  // Settings (admin only)
  sourceDomains: () => api.get<{ groups: { grp: string; label: string }[]; domains: SourceDomain[]; note: string }>(`${base}/settings/source-domains`),
  putSourceDomain: (domain: string, body: { grp: string; label?: string | null }) =>
    api.put<SourceDomain>(`${base}/settings/source-domains/${encodeURIComponent(domain)}`, body),
  deleteSourceDomain: (domain: string) => api.del<{ deleted: boolean }>(`${base}/settings/source-domains/${encodeURIComponent(domain)}`),
  brandTerms: (pub: DeskPub) => api.get<{ publication: string; terms: string[] }>(`${base}/settings/brand-terms?publication=${encodeURIComponent(pub)}`),
  putBrandTerms: (pub: DeskPub, terms: string[]) =>
    api.put<{ publication: string; terms: string[] }>(`${base}/settings/brand-terms?publication=${encodeURIComponent(pub)}`, { terms }),
  // Alerts, recipients, digest (admin only)
  alertSettings: () => api.get<AlertSettings>(`${base}/settings/alerts`),
  putAlertRule: (rule: string, body: { enabled?: boolean; params?: Record<string, number | boolean> }) =>
    api.put<AlertRuleView>(`${base}/settings/alerts/${encodeURIComponent(rule)}`, body),
  recipients: (kind: RecipientKind) => api.get<{ kind: RecipientKind; titles: RecipientTitle[] }>(`${base}/settings/recipients?kind=${kind}`),
  putRecipients: (kind: RecipientKind, pub: string, userIds: number[]) =>
    api.put<RecipientTitle>(`${base}/settings/recipients/${kind}/${encodeURIComponent(pub)}`, { user_ids: userIds }),
  digestSettings: () => api.get<DigestSettings>(`${base}/settings/digest`),
  putDigestSettings: (body: { digest_on?: boolean; alerts_email_on?: boolean }) => api.put<DigestSettings>(`${base}/settings/digest`, body),
  digestPreview: (pub: string) => api.get<DigestPreview>(`${base}/digest/preview?publication=${encodeURIComponent(pub)}`),
  digestTest: (pub: string) => api.post<{ sent: boolean; to: string }>(`${base}/digest/test?publication=${encodeURIComponent(pub)}`),
}
